import { enableMapSet } from "immer";
import { create, Mutate, StoreApi, UseBoundStore } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import { createLogger, debounce } from "@tsmono/util";

import { Capabilities, ClientStorage } from "../client/api/types";

import { AppSlice, createAppSlice, initializeAppSlice } from "./appSlice";
import { createLogSlice, initalializeLogSlice, LogSlice } from "./logSlice";
import { createLogsSlice, initializeLogsSlice, LogsSlice } from "./logsSlice";
import {
  createSampleSlice,
  initializeSampleSlice,
  SampleSlice,
} from "./sampleSlice";
import { createSearchSlice, SearchSlice } from "./searchSlice";

const log = createLogger("store");

export interface StoreState
  extends AppSlice, LogsSlice, LogSlice, SampleSlice, SearchSlice {
  // Global actions
  initialize: (capabilities: Capabilities) => void;
}

// The store is immer-wrapped, so its `setState` accepts an immer recipe
// `(state) => void`. Reflect that in the exported type so non-react callers
// can drive setState with a recipe.
export type ImmerStore = UseBoundStore<
  Mutate<StoreApi<StoreState>, [["zustand/immer", never]]>
>;

export let storeImplementation: ImmerStore | null = null;

// The data that will actually be persisted
export type PersistedState = {
  app: AppSlice["app"];
  log: LogSlice["log"];
  logs: LogsSlice["logs"];
};

// Create a proxy store that forwards calls to the real store once initialized
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- store-proxy boundary: the wrapper forwards to the real bound store, and zustand's UseBoundStore signature (callable plus statics) can't be reconstructed structurally
export const useStore = ((selector?: (state: StoreState) => unknown) => {
  if (!storeImplementation) {
    throw new Error(
      "Store accessed before initialization. Call initializeStore first."
    );
  }
  return selector ? storeImplementation(selector) : storeImplementation();
}) as UseBoundStore<StoreApi<StoreState>>;

// Initialize the store
export const initializeStore = (
  capabilities: Capabilities,
  storage?: ClientStorage
) => {
  enableMapSet();

  // Create the storage implementation
  const storageImplementation = {
    getItem: <T>(name: string): T | null => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- persisted webview/store state (#555): the host returns whatever it stored, and T is the caller's claim
      return storage ? (storage.getItem(name) as T) : null;
    },
    setItem: debounce(<T>(name: string, value: T): void => {
      if (storage) {
        storage.setItem(name, value);
      }
    }, 1000),
    removeItem: (name: string): void => {
      if (storage) {
        storage.removeItem(name);
      }
    },
  };

  // Create the actual store
  const store = create<StoreState>()(
    devtools(
      persist(
        immer((set, get, store) => ({
          // Initialize
          initialize: (capabilities) => {
            // Initialize application slices
            initializeAppSlice(set, capabilities);
            initializeLogsSlice(set);
            initalializeLogSlice(set);
            initializeSampleSlice(set);
          },

          // Create the slices and merge them in
          ...createAppSlice(set, get, store),
          ...createLogsSlice(set, get, store),
          ...createLogSlice(set, get, store),
          ...createSampleSlice(set, get, store),
          ...createSearchSlice(set),
        })),
        {
          name: "app-storage",
          storage: storageImplementation,
          partialize: (state) =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- zustand-persist types partialize as StoreState -> StoreState, but a partial slice is exactly what it is for
            ({
              app: state.app,
              log: state.log,
              logs: state.logs,
            }) as unknown as StoreState,
          version: 4,
          onRehydrateStorage: (state: StoreState) => {
            return (hydrationState, error) => {
              log.debug("REHYDRATING STATE");
              if (error) {
                log.debug("ERROR", { error });
              } else {
                log.debug("STATE", { state, hydrationState });
              }
            };
          },
        }
      )
    )
  );

  // Set the implementation and initialize it
  storeImplementation = store;
  store.getState().initialize(capabilities);

  // Purge the legacy Virtuoso list-position bags that pre-#525 sessions
  // persisted; nothing reads or clears them anymore.
  store.getState().appActions.removeBagsByPrefix("listPosition");
};
